import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../config';

const API = config.apiUrl;

const QuestionnairePage = ({ user, setCurrentAssessment }) => {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [responseOptions, setResponseOptions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadQuestions();
  }, []);

  const loadQuestions = async () => {
    try {
      const [questionsRes, optionsRes] = await Promise.all([
        axios.get(`${API}/questions`),
        axios.get(`${API}/response-options`)
      ]);
      
      setQuestions(questionsRes.data);
      setResponseOptions(optionsRes.data);
      setLoading(false);
    } catch (err) {
      console.error('Error loading questions:', err);
      setError('Erreur lors du chargement des questions.');
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionId, value) => {
    setAnswers({
      ...answers,
      [questionId]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Vérifier que toutes les questions ont une réponse
    if (Object.keys(answers).length !== 28) {
      setError('Veuillez répondre à toutes les questions avant de soumettre.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const formattedAnswers = Object.entries(answers).map(([question_id, value]) => ({
        question_id: parseInt(question_id),
        value: value
      }));

      const response = await axios.post(`${API}/assessments`, {
        user_id: user.id,
        answers: formattedAnswers
      });

      setCurrentAssessment(response.data);
      navigate('/results');
    } catch (err) {
      console.error('Error submitting assessment:', err);
      setError('Erreur lors de la soumission. Veuillez réessayer.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setSubmitting(false);
    }
  };

  const progress = (Object.keys(answers).length / 28) * 100;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Chargement du questionnaire...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4" data-testid="questionnaire-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-slate-800">Questionnaire Stress Management Test</h1>
              <p className="text-slate-600 mt-1">
                Répondant : {user.first_name} {user.last_name}
              </p>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-blue-600">{Object.keys(answers).length}/28</div>
              <div className="text-sm text-slate-500">questions</div>
            </div>
          </div>
          
          {/* Progress bar */}
          <div className="w-full bg-slate-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-xl mb-6" data-testid="error-message">
            {error}
          </div>
        )}

        {/* Question Préliminaire */}
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-6 mb-6">
          <h2 className="font-semibold text-purple-900 mb-3 text-lg">Question préliminaire</h2>
          <p className="text-purple-800 font-medium">
            Avez-vous déjà vécu une situation stressante et qu'avez-vous fait pour vous en sortir ?
          </p>
          <p className="text-purple-700 text-sm mt-2 italic">
            Prenez un moment pour y réfléchir avant de commencer le questionnaire.
          </p>
        </div>

        {/* Instructions */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-6">
          <h2 className="font-semibold text-blue-900 mb-2">Instructions</h2>
          <p className="text-blue-800 text-sm">
            Pour chaque énoncé, indiquez à quelle fréquence vous avez utilisé cette stratégie pour faire face à une situation stressante récente.
          </p>
        </div>

        {/* Questions Form */}
        <form onSubmit={handleSubmit}>
          <div className="space-y-6">
            {questions.map((question, index) => {
              const isAnswered = answers[question.id] !== undefined;
              const delay = index * 50; // Délai progressif pour chaque question
              
              return (
                <div 
                  key={question.id} 
                  className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-all duration-500 transform"
                  style={{
                    animation: `fadeInUp 0.6s ease-out ${delay}ms both`,
                    opacity: isAnswered ? 1 : 0.95
                  }}
                  data-testid={`question-${question.id}`}
                >
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className={`inline-block text-slate-700 text-sm font-semibold px-3 py-1 rounded-full transition-all duration-300 ${
                        isAnswered 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-slate-100'
                      }`}>
                        Question {index + 1}
                        {isAnswered && (
                          <span className="ml-2 inline-block">✓</span>
                        )}
                      </span>
                    </div>
                    <p className="text-slate-800 font-medium text-lg">{question.text}</p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    {responseOptions.map((option) => {
                      const isSelected = answers[question.id] === option.value;
                      
                      return (
                        <label
                          key={option.value}
                          className={`cursor-pointer border-2 rounded-lg p-4 transition-all duration-300 transform hover:scale-105 ${
                            isSelected
                              ? 'border-blue-600 bg-blue-50 scale-105 shadow-md'
                              : 'border-slate-200 hover:border-blue-300 bg-white hover:shadow-sm'
                          }`}
                          data-testid={`question-${question.id}-option-${option.value}`}
                        >
                          <input
                            type="radio"
                            name={`question-${question.id}`}
                            value={option.value}
                            checked={isSelected}
                            onChange={() => handleAnswerChange(question.id, option.value)}
                            className="sr-only"
                          />
                          <div className="flex items-center justify-between">
                            <span className={`text-sm font-semibold transition-colors ${
                              isSelected ? 'text-blue-700' : 'text-slate-700'
                            }`}>
                              {option.label}
                            </span>
                            <span className={`text-xl font-bold transition-colors ${
                              isSelected ? 'text-blue-600' : 'text-slate-400'
                            }`}>
                              {option.value}
                            </span>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Submit button */}
          <div className="mt-8 bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div className="text-sm text-slate-600">
                {Object.keys(answers).length === 28 ? (
                  <span className="text-green-600 font-semibold">✓ Toutes les questions ont été répondues</span>
                ) : (
                  <span>Il reste {28 - Object.keys(answers).length} question(s) à répondre</span>
                )}
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-8 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="submit-questionnaire-btn"
              >
                {submitting ? 'Soumission...' : 'Soumettre mes réponses'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QuestionnairePage;
